// pages/ProductResultPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { getProductAnalysis, getScannerPrefs } from '../api/endpoints';
import { API_BASE_URL } from '../api/config';
import { isBarcodeRequiredError, isProductIngredientsPendingError } from '../api/productErrors';
import { formatTeamDeadline } from '../utils/teamDeadline';
import { isUnverifiedList, labelSourceNote } from '../utils/labelSource';
import useFavoriteProduct from '../hooks/useFavoriteProduct';
import { postScannerResultView } from '../api/telemetry';
import AIDisclaimerFooter from '../components/AIDisclaimerFooter';
import { ProductSearchModal } from '../components/ProductSearchModal';
import { getErrorMessage } from '../utils/errors';
import {
  comedogenicScore,
  getIrritantLevel,
  ingredientDisplayName,
  labelize,
} from '../utils/scannerIngredient';
import type { ProductAnalysisSnapshot, ScannerIngredient } from '../types/product';
import { knownBreakdownEntries } from '../utils/scoreBreakdown';

/* ---------------- Types ---------------- */
type LocationState = { barcode?: string; source?: 'db' | 'ai' | 'catalog' };
type AnalysisResp = Awaited<ReturnType<typeof getProductAnalysis>>;
type PrefsOut = Awaited<ReturnType<typeof getScannerPrefs>>;
type AnalysisData = ProductAnalysisSnapshot;

/* ---------------- Icons (inline SVG) ---------------- */
const svgBase = {
  width: 18, height: 18, fill: 'none' as const,
  stroke: 'currentColor', strokeWidth: '1.8', strokeLinecap: 'round' as const,
};

const IconShield = () => (
  <svg viewBox="0 0 24 24" {...svgBase}>
    <path d="M12 3l7 3v5c0 5-3.5 9-7 10-3.5-1-7-5-7-10V6l7-3Z" />
    <path d="M9.5 12.5l1.8 1.8 3.2-3.6" />
  </svg>
);
const IconChart = () => (
  <svg viewBox="0 0 24 24" {...svgBase}>
    <path d="M4 19h16M8 18V9M12 18V5M16 18v-7" />
  </svg>
);
const IconWarn = () => (
  <svg viewBox="0 0 24 24" {...svgBase}>
    <path d="M10.3 2.7 1.6 17.7A2 2 0 0 0 3.3 21h17.4a2 2 0 0 0 1.7-3.3L13.7 2.7a2 2 0 0 0-3.4 0Z" />
    <path d="M12 8v5M12 17h.01" />
  </svg>
);
const IconSun = () => (
  <svg viewBox="0 0 24 24" {...svgBase}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);
const IconBolt = () => (
  <svg viewBox="0 0 24 24" {...svgBase}>
    <path d="M13 2 3 14h7l-1 8 11-14h-7l0-6Z" />
  </svg>
);
const IconBeaker = () => (
  <svg viewBox="0 0 24 24" {...svgBase}>
    <path d="M6 2h12M9 2v5l-5 9a4 4 0 0 0 3.5 6h9a4 4 0 0 0 3.5-6L15 7V2" />
    <path d="M8 14h8" />
  </svg>
);
const IconLeaf = () => (
  <svg viewBox="0 0 24 24" {...svgBase}>
    <path d="M5 19c7 0 14-7 14-14 0 0-7-1-12 4s-5 10-2 12Z" />
    <path d="M6 17c1-3 5-7 9-9" />
  </svg>
);
const IconShopping = () => (
  <svg viewBox="0 0 24 24" {...svgBase} strokeLinejoin="round">
    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
    <path d="M3 6h18" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);

/* ---------------- Product SVG illustration ---------------- */
function ProductArt({ size = 140, className = '' }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={Math.round(size * 1.3)}
      viewBox="0 0 120 156"
      aria-hidden="true"
      className={`block ${className}`}
      style={{ filter: 'drop-shadow(0 2px 8px rgba(31,36,33,.10))' }}
    >
      <defs>
        <linearGradient id="pa-g1" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#72CDD0" />
          <stop offset="100%" stopColor="#2AA4A6" />
        </linearGradient>
        <linearGradient id="pa-g2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#EAF7F7" />
          <stop offset="100%" stopColor="#F8F4EE" />
        </linearGradient>
      </defs>
      <g transform="translate(8,6)">
        <rect x="30" y="0" width="44" height="16" rx="4" fill="url(#pa-g1)" />
        <rect x="22" y="16" width="60" height="10" rx="5" fill="url(#pa-g2)" />
        <rect x="14" y="26" width="76" height="112" rx="18" fill="url(#pa-g2)" stroke="rgba(42,164,166,0.30)" />
        <rect x="14" y="26" width="76" height="64" rx="18" fill="url(#pa-g1)" opacity=".14" />
        <circle cx="52" cy="102" r="12" fill="url(#pa-g1)" opacity=".2" />
        <path d="M52 86c8 12 8 20 0 28-8-8-8-16 0-28Z" fill="url(#pa-g1)" />
      </g>
    </svg>
  );
}

/* ---------------- Brand component primitives ---------------- */

type Tone = 'ok' | 'warn' | 'danger' | 'neutral';

function KivoPill({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: Tone }) {
  const mod = tone === 'ok' ? 'pill--ok' : tone === 'warn' ? 'pill--warn' : tone === 'danger' ? 'pill--danger' : '';
  return <span className={`pill ${mod}`.trim()}>{children}</span>;
}

function KivoBadge({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: Tone }) {
  const mod = tone === 'ok' ? 'pill--ok' : tone === 'warn' ? 'pill--warn' : tone === 'danger' ? 'pill--danger' : '';
  return <span className={`pill pill--sm ${mod}`.trim()}>{children}</span>;
}

function KivoCard({
  title,
  children,
  right,
  icon,
}: {
  title: string;
  children: React.ReactNode;
  right?: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <section
      className="card card--bordered card--flat"
      style={{ padding: 'var(--s-5)', overflow: 'hidden', minWidth: 0 }}
    >
      <div
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)',
          alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 'var(--s-3)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)', minWidth: 0 }}>
          {icon && (
            <span style={{ color: 'var(--c-kivo-teal)', flexShrink: 0, display: 'flex' }}>
              {icon}
            </span>
          )}
          <h3
            style={{
              font: '600 var(--fs-small)/1 var(--f-text)',
              letterSpacing: '0.04em', textTransform: 'uppercase',
              color: 'var(--ink)', margin: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {title}
          </h3>
        </div>
        {right && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)', alignItems: 'center' }}>
            {right}
          </div>
        )}
      </div>
      <div style={{ minWidth: 0 }}>{children}</div>
    </section>
  );
}

function KivoStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card card--bordered card--flat" style={{ padding: 'var(--s-3)' }}>
      <div className="t-small t-mute">{label}</div>
      <div
        style={{
          fontFamily: 'var(--f-display)', fontSize: 'var(--fs-h3)',
          fontWeight: 500, marginTop: 'var(--s-1)', lineHeight: 1.1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* ---------------- Score ring (brand conic component) ---------------- */
function scoreTone(score: number): { label: string; fill: string; track: string } {
  if (score >= 75) return { label: 'Great',     fill: 'var(--c-deep-teal)',       track: 'var(--c-teal-weak)'           };
  if (score >= 50) return { label: 'Okay',      fill: 'var(--c-kivo-teal)',       track: 'var(--c-teal-weak)'           };
  if (score >= 30) return { label: 'Caution',   fill: 'rgba(244,168,142,.85)',    track: 'rgba(244,168,142,.22)'        };
  return            { label: 'High risk',  fill: 'var(--c-coral)',          track: 'rgba(244,168,142,.22)'        };
}

function ScoreRing({ score, size = 140 }: { score: number; size?: number }) {
  const pct = Math.max(0, Math.min(100, score));
  const tone = scoreTone(pct);
  return (
    <div
      className="ring"
      style={{
        '--pct': String(Math.round(pct)),
        '--size': `${size}px`,
        '--ring-inset': '10px',
        '--ring-fill': tone.fill,
        '--ring-track': tone.track,
      } as React.CSSProperties}
    >
      <div className="ring__inner">
        <div className="ring__num">{Math.round(pct)}</div>
        <div className="ring__cap">Score</div>
        <div className="t-small t-mute" style={{ marginTop: 'var(--s-1)' }}>{tone.label}</div>
      </div>
    </div>
  );
}

/* ---------------- Utilities ---------------- */
const fmtPct = (v?: number) => (typeof v === 'number' && Number.isFinite(v) ? `${Math.round(v)}%` : '—');
const prettySource = (s?: 'db' | 'ai' | 'catalog') =>
  s === 'ai' ? 'AI' : s === 'db' ? 'DB' : s === 'catalog' ? 'Catalog' : '—';
const formatWhen = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

/* =================================================================== */
/* Page                                                                 */
/* =================================================================== */
export default function ProductResultPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const loc = useLocation();
  const state = (loc.state ?? {}) as LocationState;
  const [searchParams] = useSearchParams();

  // History re-open uses a hard navigation (no router state), so the pair/
  // barcode is passed via the URL. `?barcode=` ('' = barcode-less slug product)
  // is treated exactly like navigation state.
  const urlBarcode = searchParams.get('barcode');
  const routeBarcode = state.barcode ?? urlBarcode ?? undefined;

  const [barcodeInput, setBarcodeInput] = useState(state.barcode ?? '');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** 409 ingredients_pending: the team is still writing the ingredient list. */
  const [pending, setPending] = useState(false);
  const [pendingBy, setPendingBy] = useState<string | null>(null);
  const [data, setData] = useState<AnalysisResp | null>(null);
  const [searchingProduct, setSearchingProduct] = useState<{ keyword: string; title: string } | null>(null);

  const [prefs, setPrefs] = useState<PrefsOut | null>(null);
  const hasPrefs =
    prefs !== null &&
    (prefs.allergies.length > 0 ||
      prefs.acne_prone ||
      prefs.ethical_mode !== 'off' ||
      prefs.cautious_actives);

  useEffect(() => {
    let alive = true;
    getScannerPrefs()
      .then((p) => { if (alive) setPrefs(p); })
      .catch(() => { if (alive) setPrefs(null); });
    return () => { alive = false; };
  }, []);

  const effectiveBarcode = useMemo(
    () => ((routeBarcode ?? barcodeInput) || '').replace(/\D/g, '').trim(),
    [routeBarcode, barcodeInput],
  );
  // routeBarcode === '' means a barcode-less product (slug-identified, e.g.
  // created via image scan): the backend analyzes those by product id alone.
  // Only an undefined route barcode (direct URL visit) requires confirmation.
  const isBarcodeless = routeBarcode === '';
  // A direct visit (no route barcode) first asks by id alone: a barcode-less
  // or private product answers right away; one with a barcode answers
  // `missing barcode`, and only then is the user asked for it.
  const [barcodeRequired, setBarcodeRequired] = useState(false);
  const probing = routeBarcode === undefined && effectiveBarcode === '';
  const canFetch =
    !!id && /^\d+$/.test(id) &&
    (isBarcodeless || probing || (effectiveBarcode.length >= 8 && effectiveBarcode.length <= 14));
  const needsConfirm = routeBarcode === undefined && barcodeRequired;

  useEffect(() => {
    if (!canFetch) return;
    let alive = true;
    setLoading(true);
    setErr(null);
    setPending(false);
    setPendingBy(null);
    setData(null);

    void postScannerResultView(id ? Number(id) : undefined, effectiveBarcode).catch(() => { /* telemetry optional */ });

    getProductAnalysis(Number(id), effectiveBarcode)
      .then((res) => { if (alive) setData(res); })
      .catch((error_: unknown) => {
        if (!alive) return;
        if (isBarcodeRequiredError(error_)) setBarcodeRequired(true);
        else if (isProductIngredientsPendingError(error_)) {
          setPending(true);
          setPendingBy(formatTeamDeadline(error_.deadlineAt));
        } else setErr(getErrorMessage(error_, 'Failed to load analysis'));
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id, effectiveBarcode, canFetch]);

  const a = data?.analysis as AnalysisData | undefined;
  const score = a?.score ?? a?.score_raw ?? 0;
  // A pending product has no analysis yet; the route id still lets the owner
  // favorite it (and so pick it for a comparison once the team is done).
  const productId = data?.product_id ?? (pending && id ? Number(id) : undefined);
  const heroImage = data?.image_url;
  const heroImageSrc = heroImage
    ? heroImage.startsWith('http')
      ? heroImage
      : new URL(heroImage.startsWith('/') ? heroImage : `/${heroImage}`, API_BASE_URL).toString()
    : null;
  const canFavorite = typeof productId === 'number' && Number.isFinite(productId);
  const { favorited, loading: favLoading, toggle } = useFavoriteProduct({
    productId,
    disabled: !canFavorite,
    initial: data?.is_favorite,
  });

  const potentialIrritants = a?.potential_irritants ?? [];
  const photosensitizers   = a?.photosensitizers   ?? [];
  const actives            = a?.actives            ?? [];
  const counts             = a?.counts;

  const allergenHits = a?.allergen_hits ?? [];
  const selectedAllergens = prefs?.allergies ?? [];
  const showAllergySection = selectedAllergens.length > 0 && allergenHits.length > 0;

  const showIrritants = potentialIrritants.length > 0;
  const showPhotos    = photosensitizers.length > 0;
  const showActives   = actives.length > 0;

  const skinFit = a?.skin_fit;
  const highlights = a?.highlights ?? [];
  const productQuality = a?.product_quality;
  const analysisQuality = a?.analysis_quality;
  const showHighlights = highlights.length > 0;
  const showQuality =
    !!productQuality &&
    ((productQuality.certifications?.length ?? 0) > 0 ||
      typeof productQuality.inci_quality_score === 'number' ||
      typeof productQuality.clean_beauty_score === 'number' ||
      typeof productQuality.spf === 'number');

  const prefersReduced = useReducedMotion() ?? false;
  const activePrefs = hasPrefs ? prefs : null;
  const qualityDetails = showQuality ? productQuality : null;

  return (
    <>
      <div className="kivo-page">
        <div className="stack-5" style={{ maxWidth: 980, marginInline: 'auto', width: '100%', paddingBlock: 'var(--s-5)' }}>

          {/* ── Awaiting team review (no analysis yet) ── */}
          {pending && !loading && (
            <KivoCard title="Checking ingredients" icon={<IconBeaker />}>
              <p className="t-small" style={{ margin: 0 }}>
                Our team is checking the ingredient list of this product. Its analysis appears here once
                it&apos;s ready, {pendingBy ? `by ${pendingBy}` : 'usually within 72 hours'}. No extra credits are needed.
              </p>
              <button
                type="button"
                onClick={toggle}
                disabled={!canFavorite || favLoading}
                className={`btn btn--sm ${favorited ? 'btn--coral' : 'btn--ghost'}`}
                style={{ marginTop: 'var(--s-3)' }}
              >
                {favorited ? 'Remove from favorites' : 'Add to favorites'}
              </button>
            </KivoCard>
          )}

          {/* ── Hero card ── */}
          {!pending && (
          <div
            className="card card--bordered card--flat"
            style={{ position: 'relative', overflow: 'hidden', borderRadius: 'var(--r-xl)' }}
          >
            <div
              className="scan-pattern"
              style={{ position: 'absolute', inset: 0, opacity: 0.35, pointerEvents: 'none' }}
              aria-hidden="true"
            />
            <div style={{ position: 'relative', zIndex: 1, padding: 'clamp(var(--s-3), 3vw, var(--s-5))' }}>
              <div className="hero-split">

                {/* Ring + product art — first in DOM so it appears at top on mobile */}
                <div
                  className="sm:col-start-2 sm:row-start-1"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: 'var(--s-3)', flexShrink: 0,
                  }}
                >
                  <ScoreRing score={typeof score === 'number' ? score : 0} />
                  {heroImageSrc ? (
                    <div
                      className="frame"
                      style={{ width: 112, height: 112, alignItems: 'center', justifyContent: 'center', display: 'flex' }}
                    >
                      <img
                        src={heroImageSrc}
                        alt={a?.name ?? 'Product'}
                        style={{ maxHeight: 96, maxWidth: 88, objectFit: 'contain' }}
                      />
                    </div>
                  ) : (
                    <ProductArt size={112} />
                  )}
                </div>

                {/* Metadata + actions — second in DOM, placed in col 1 row 1 on desktop */}
                <div style={{ minWidth: 0 }} className="sm:col-start-1 sm:row-start-1">
                  <span className="t-label">
                    {state.source === 'catalog' ? 'Product Analysis' : 'Instant Results'}
                  </span>
                  <h1
                    style={{
                      margin: 0,
                      marginTop: 'var(--s-1)',
                      font: `500 clamp(18px, 5vw, 32px)/1.2 var(--f-display)`,
                      letterSpacing: '-0.005em',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {a?.brand ? `${a.brand} — ` : ''}
                    <em className="t-italic">{a?.name ?? 'Product Safety Analysis'}</em>
                  </h1>

                  <div
                    style={{
                      display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                      gap: 'var(--s-2)', marginTop: 'var(--s-3)',
                    }}
                  >
                    <span className="pill">
                      <span className="t-mute" style={{ fontStyle: 'normal' }}>Barcode</span>
                      &nbsp;{data?.barcode ?? '—'}
                    </span>
                    {state.source ? <span className="pill">{prettySource(state.source)} source</span> : null}
                    <QualityBadge quality={analysisQuality} />
                    <SkinFitPill fit={skinFit} />
                    <span className="t-small t-mute">Analyzed: {formatWhen(a?.analyzed_at)}</span>
                  </div>
                  {labelSourceNote(data?.label_source) && (
                    <p
                      className={isUnverifiedList(data?.label_source) ? 'notice' : 't-small t-mute'}
                      role={isUnverifiedList(data?.label_source) ? 'status' : undefined}
                      style={{ margin: 0, marginTop: 'var(--s-2)' }}
                    >
                      {labelSourceNote(data?.label_source)}
                    </p>
                  )}

                  <div
                    style={{
                      display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)',
                      marginTop: 'var(--s-4)',
                    }}
                  >
                    <button
                      type="button"
                      onClick={toggle}
                      disabled={!canFavorite || favLoading}
                      className={`btn btn--sm ${favorited ? 'btn--coral' : 'btn--ghost'}`}
                      style={{ flex: '1 1 auto', justifyContent: 'center' }}
                    >
                      <svg
                        width={16} height={16} viewBox="0 0 24 24"
                        fill={favorited ? 'currentColor' : 'none'}
                        stroke="currentColor" strokeWidth="1.8"
                        strokeLinecap="round" strokeLinejoin="round"
                      >
                        <path d="M12 21s-6.4-4.35-9.2-8.06C1.02 11.66 1 8.65 3.2 6.4 5.4 4.2 8.7 4.1 10.9 6.3L12 7.41l1.1-1.1c2.2-2.2 5.5-2.1 7.7.1 2.2 2.24 2.18 5.26.2 6.54C18.4 16.65 12 21 12 21Z" />
                      </svg>
                      {favorited ? 'Remove from favorites' : 'Add to favorites'}
                    </button>

                    {data && (
                      <button
                        type="button"
                        onClick={() =>
                          { setSearchingProduct({
                            keyword: `${a?.brand ?? ''} ${a?.name ?? ''}`.trim(),
                            title: a?.name ?? 'Product',
                          }); }
                        }
                        className="btn btn--primary btn--sm"
                        style={{ flex: '1 1 auto', justifyContent: 'center' }}
                      >
                        <IconShopping />
                        Find to buy
                      </button>
                    )}
                    {!canFavorite && (
                      <span className="t-small t-mute">Product ID unavailable — favorites disabled.</span>
                    )}
                  </div>

                  {activePrefs && (
                    <div
                      style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)', marginTop: 'var(--s-3)' }}
                    >
                      {activePrefs.acne_prone && <KivoPill tone="warn">Breakout-prone</KivoPill>}
                      {activePrefs.cautious_actives && <KivoPill tone="warn">Gentle actives</KivoPill>}
                      {activePrefs.ethical_mode !== 'off'
                        ? <KivoPill>Ethical: {labelize(activePrefs.ethical_mode)}</KivoPill>
                        : null}
                      {selectedAllergens.length > 0 && (
                        <KivoPill>
                          {selectedAllergens.length} allergen{selectedAllergens.length > 1 ? 's' : ''} selected
                        </KivoPill>
                      )}
                    </div>
                  )}
                </div>


              </div>
            </div>
          </div>
          )}

          {/* ── Confirm barcode ── */}
          {needsConfirm && (
            <KivoCard title="Confirm Barcode" icon={<IconBeaker />}>
              <p className="t-small t-mute" style={{ marginBottom: 'var(--s-2)' }}>
                Enter the scanned barcode to view this result.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }}>
                <input
                  style={{
                    flex: 1, borderRadius: 'var(--r-sm)', border: '1px solid var(--rule)',
                    padding: 'var(--s-3)', font: 'var(--fs-body)/1.4 var(--f-text)',
                    background: 'var(--c-cream-weak)', color: 'var(--ink)',
                  }}
                  value={barcodeInput}
                  onChange={(e) => { setBarcodeInput(e.target.value.replace(/\D/g, '')); }}
                  placeholder="Barcode"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  disabled={effectiveBarcode.length < 8}
                  onClick={() => {
                    const v = effectiveBarcode;
                    if (v.length >= 8) nav('.', { replace: true, state: { ...state, barcode: v } });
                  }}
                >
                  Apply
                </button>
              </div>
              <p className="t-small t-mute" style={{ marginTop: 'var(--s-1)' }}>
                Digits only. EAN-8 / UPC-A / EAN-13 / GTIN-14.
              </p>
            </KivoCard>
          )}

          {/* ── Loading ── */}
          {loading && (
            <KivoCard title="Loading" icon={<IconBeaker />}>
              <div
                style={{
                  display: 'grid', placeItems: 'center',
                  padding: 'var(--s-6) 0', gap: 'var(--s-3)',
                }}
              >
                <div className="kv-spinner" />
                <p className="t-small t-mute">Loading analysis…</p>
              </div>
            </KivoCard>
          )}

          {/* ── Error ── */}
          {err && !loading && (
            <div
              className="card card--conflict"
              style={{ padding: 'var(--s-5)', font: 'var(--fs-small)/1.5 var(--f-text)' }}
            >
              {err}
            </div>
          )}

          {/* ── Main analysis content ── */}
          {data && a && !loading && !err && (
            <>
              <KivoCard
                title="Safety Analysis"
                icon={<IconShield />}
                right={<HeaderPills counts={counts} />}
              >
                {activePrefs && (
                  <div
                    className="card card--sand card--flat"
                    style={{ padding: 'var(--s-3)', marginBottom: 'var(--s-4)' }}
                  >
                    <p
                      style={{
                        font: '500 var(--fs-small)/1 var(--f-text)', color: 'var(--ink)',
                        marginBottom: 'var(--s-2)', marginTop: 0,
                      }}
                    >
                      Personalized for you:
                    </p>
                    <ul
                      className="t-small"
                      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-1)', margin: 0, padding: 0, listStyle: 'none', color: 'var(--ink-muted)' }}
                    >
                      {activePrefs.allergies.length > 0 && (
                        <li>Checks your chosen allergen list against ingredients</li>
                      )}
                      {activePrefs.acne_prone && <li>Highlights comedogenic ingredients (0–5 scale)</li>}
                      {activePrefs.cautious_actives && <li>More conservative on potential irritants</li>}
                      {activePrefs.ethical_mode !== 'off' && (
                        <li>Notes ethical preference ({labelize(activePrefs.ethical_mode)})</li>
                      )}
                    </ul>
                  </div>
                )}

                <div className="g-3">
                  <KivoStat label="Score"      value={fmtPct(score)} />
                  <KivoStat label="Product ID" value={String(data.product_id)} />
                  <KivoStat label="Source"     value={prettySource(state.source)} />
                </div>
              </KivoCard>

              <FilteredBreakdown
                title="Score breakdown"
                icon={<IconChart />}
                breakdown={a.score_breakdown}
                prefersReduced={prefersReduced}
              />

              {showAllergySection && (
                <KivoCard
                  title="On your avoid list"
                  icon={<IconWarn />}
                  right={<KivoPill tone="danger">{allergenHits.length}</KivoPill>}
                >
                  <TagCloud items={allergenHits} tone="danger" />
                </KivoCard>
              )}

              <div style={{ display: 'grid', gap: 'var(--s-4)', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                {showIrritants && (
                  <KivoCard
                    title="Potential irritants"
                    icon={<IconWarn />}
                    right={<KivoPill tone="warn">{potentialIrritants.length}</KivoPill>}
                  >
                    <TagCloud items={potentialIrritants} tone="warn" />
                  </KivoCard>
                )}

                {showPhotos && (
                  <KivoCard
                    title="Photosensitizers"
                    icon={<IconSun />}
                    right={<KivoPill tone="warn">{photosensitizers.length}</KivoPill>}
                  >
                    <TagCloud items={photosensitizers} tone="warn" />
                  </KivoCard>
                )}

                {showActives && (
                  <KivoCard
                    title="Actives"
                    icon={<IconBolt />}
                    right={<KivoPill tone="ok">{actives.length}</KivoPill>}
                  >
                    <TagCloud items={actives} tone="ok" />
                  </KivoCard>
                )}
              </div>

              {showHighlights && (
                <KivoCard title="What it does" icon={<IconBolt />}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-2)' }}>
                    {highlights.map((h, i) => (
                      <div
                        key={`${h.target}-${i}`}
                        style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--s-2)' }}
                      >
                        <KivoPill tone="ok">{labelize(h.target)}</KivoPill>
                        {h.evidence_level && (
                          <span className="t-small t-mute">{labelize(h.evidence_level)} evidence</span>
                        )}
                        {(h.ingredients?.length ?? 0) > 0 && (
                          <span className="t-small t-mute">· {(h.ingredients ?? []).slice(0, 4).join(', ')}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </KivoCard>
              )}

              {qualityDetails && (
                <KivoCard title="Product quality" icon={<IconLeaf />}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)', alignItems: 'center' }}>
                    {typeof qualityDetails.inci_quality_score === 'number' && (
                      <KivoPill tone="ok">Quality {qualityDetails.inci_quality_score}/100</KivoPill>
                    )}
                    {typeof qualityDetails.clean_beauty_score === 'number' && (
                      <KivoPill>Clean beauty {Math.round(qualityDetails.clean_beauty_score)}</KivoPill>
                    )}
                    {typeof qualityDetails.spf === 'number' && qualityDetails.spf > 0 && (
                      <KivoPill>SPF {qualityDetails.spf}</KivoPill>
                    )}
                    {(qualityDetails.certifications ?? []).map((c) => (
                      <KivoPill key={c}>{labelize(c)}</KivoPill>
                    ))}
                  </div>
                </KivoCard>
              )}

              <IngredientsAccordion ingredients={a.ingredients ?? []} />

              {a.ethical?.mode && (
                <KivoCard title="Ethical profile" icon={<IconLeaf />}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)' }}>
                    <KivoPill>Preference: {labelize(a.ethical.mode)}</KivoPill>
                    {a.ethical.non_cruelty_detected
                      ? <KivoPill tone="ok">Cruelty-free compatible</KivoPill>
                      : null}
                  </div>
                </KivoCard>
              )}

              <div style={{ paddingBottom: 'var(--s-2)' }}>
                <button
                  type="button"
                  onClick={() => { nav('/scanner'); }}
                  className="btn btn--ghost btn--sm"
                >
                  ← New scan
                </button>
              </div>
            </>
          )}

        </div>
      </div>

      {searchingProduct && (
        <ProductSearchModal
          keyword={searchingProduct.keyword}
          titleOverride={searchingProduct.title}
          onClose={() => { setSearchingProduct(null); }}
        />
      )}

      <AIDisclaimerFooter />
    </>
  );
}

/* ---------------- INCI-enriched, adaptive result signals ---------------- */

// Data-provenance badge: only shown when the backend reports a tier.
function QualityBadge({ quality }: { quality?: string }) {
  if (!quality) return null;
  const map: Record<string, { label: string; tone?: 'ok' | 'warn' }> = {
    inci_verified: { label: 'INCI verified', tone: 'ok' },
    partial: { label: 'Partial data' },
    estimated: { label: 'Estimated' },
  };
  if (!(quality in map)) return null;
  const cfg = map[quality];
  return <KivoPill tone={cfg.tone}>{cfg.label}</KivoPill>;
}

// Skin-type fit chip — only when an effective skin type produced a verdict.
function SkinFitPill({ fit }: { fit?: string }) {
  if (!fit || fit === 'neutral') return null;
  const tone = fit === 'suitable' ? 'ok' : fit === 'avoid' ? 'warn' : undefined;
  const label = fit === 'suitable' ? 'Suits your skin' : fit === 'avoid' ? 'Caution for your skin' : labelize(fit);
  return <KivoPill tone={tone}>{label}</KivoPill>;
}

/* ---------------- Header pills (skip zeroes) ---------------- */
function HeaderPills({
  counts,
}: {
  counts?: { safe: number; potential_irritants: number; comedogenic: number };
}) {
  if (!counts) return null;
  return (
    <>
      {counts.safe > 0 && (
        <KivoPill tone="ok">Safe: {counts.safe}</KivoPill>
      )}
      {counts.potential_irritants > 0 && (
        <KivoPill tone="warn">Irritants: {counts.potential_irritants}</KivoPill>
      )}
      {counts.comedogenic > 0 && (
        <KivoPill tone="danger">
          Comedogenic: {counts.comedogenic}
        </KivoPill>
      )}
    </>
  );
}

/* ---------------- Score breakdown (filters zeros) ---------------- */
function FilteredBreakdown({
  title,
  icon,
  breakdown,
  prefersReduced,
}: {
  title: string;
  icon: React.ReactNode;
  breakdown?: Record<string, number>;
  prefersReduced: boolean;
}) {
  if (!breakdown) return null;
  const nonZero = Object.fromEntries(Object.entries(breakdown).filter(([, v]) => Math.abs(v) > 0));
  if (Object.keys(nonZero).length === 0) return null;
  return (
    <KivoCard title={title} icon={icon}>
      <Breakdown breakdown={nonZero} prefersReduced={prefersReduced} />
    </KivoCard>
  );
}

function Breakdown({
  breakdown,
  prefersReduced,
}: {
  breakdown?: Record<string, number>;
  prefersReduced: boolean;
}) {
  if (!breakdown) {
    return <p className="t-small t-mute">No breakdown available.</p>;
  }

  const iconFor = (k: string) => {
    const n = k.toLowerCase();
    if (n.includes('irritat'))                      return <IconWarn />;
    if (n.includes('photo'))                        return <IconSun />;
    if (n.includes('active') || n.includes('bonus')) return <IconBolt />;
    if (n.includes('preserv') || n.includes('fragrance')) return <IconBeaker />;
    if (n.includes('ethical'))                      return <IconLeaf />;
    if (n.includes('acne') || n.includes('allergen')) return <IconWarn />;
    return <IconChart />;
  };

  const entries = knownBreakdownEntries(breakdown).map(([k, v]) => {
    const isBonus = k.includes('bonus');
    const label   = labelize(k.replace(/_penalty|_bonus/g, ''));
    const value   = typeof v === 'number' ? v : 0;
    const tone: Tone = isBonus ? 'ok' : value >= 12 ? 'danger' : value > 0 ? 'warn' : 'neutral';
    return { key: k, label, value, isBonus, tone };
  });

  const maxVal = Math.max(1, ...entries.map((e) => Math.abs(e.value)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)', minWidth: 0 }}>
      {entries.map((e) => {
        const width      = Math.min(100, Math.round((Math.abs(e.value) / maxVal) * 100));
        const fillClass  = e.isBonus ? 'breakdown-bar__fill--ok'
          : e.tone === 'danger'      ? 'breakdown-bar__fill--danger'
          :                            'breakdown-bar__fill--warn';
        const numColor   = e.isBonus
          ? 'var(--c-deep-teal)' : 'var(--ink-muted)';

        return (
          <div key={e.key} style={{ minWidth: 0 }}>
            <div
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 'var(--s-1)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)', minWidth: 0, color: 'var(--c-kivo-teal)' }}>
                {iconFor(e.key)}
                <span
                  style={{
                    font: '500 var(--fs-small)/1 var(--f-text)',
                    color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {e.label}
                </span>
              </div>
              <span style={{ font: '600 var(--fs-small)/1 var(--f-text)', color: numColor, flexShrink: 0, paddingLeft: 'var(--s-2)' }}>
                {e.isBonus ? '+' : '−'}{Math.abs(e.value)}
              </span>
            </div>
            <div className="breakdown-bar" aria-hidden="true">
              <motion.div
                className={`breakdown-bar__fill ${fillClass}`}
                initial={{ width: prefersReduced ? `${width}%` : 0 }}
                animate={{ width: `${width}%` }}
                transition={prefersReduced ? { duration: 0 } : { type: 'spring', stiffness: 260, damping: 28 }}
              />
            </div>
          </div>
        );
      })}
      <p className="t-small t-mute" style={{ margin: 0 }}>
        Penalties reduce the score; bonuses increase it.
      </p>
    </div>
  );
}

/* ---------------- Tag cloud ---------------- */
function TagCloud({ items, tone }: { items: string[]; tone: Tone }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)', minWidth: 0 }}>
      {items.map((x, i) => (
        <KivoPill key={`${x}-${i}`} tone={tone}>{x}</KivoPill>
      ))}
    </div>
  );
}

/* ---------------- Ingredients accordion ---------------- */
function IngredientsAccordion({ ingredients }: { ingredients: ScannerIngredient[] }) {
  const [open, setOpen] = useState(false);
  const count = ingredients.length;

  return (
    <div className="card card--bordered card--flat" style={{ overflow: 'hidden', padding: 0 }}>
      <button
        type="button"
        onClick={() => { setOpen((s) => !s); }}
        style={{
          display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--s-4) var(--s-5)', cursor: 'pointer',
          background: 'none', border: 'none', textAlign: 'left',
        }}
        aria-expanded={open}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-3)', color: 'var(--c-kivo-teal)' }}>
          <IconBeaker />
          <span
            style={{
              font: '600 var(--fs-small)/1 var(--f-text)',
              letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ink)',
            }}
          >
            Ingredients (full list)
          </span>
          <span className="t-small t-mute">{count} total</span>
        </div>
        <Chevron open={open} />
      </button>

      <motion.div
        initial={false}
        animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }}
        style={{ overflow: 'hidden', borderTop: open ? '1px solid var(--rule)' : 'none' }}
      >
        <div style={{ padding: 'var(--s-5)', paddingTop: 'var(--s-4)' }}>
          {count ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 'var(--s-2)', minWidth: 0,
              }}
            >
              {ingredients.map((ing, i) => (
                <IngredientRow key={`${ing.id ?? i}-${ing.inci_name ?? i}`} ing={ing} />
              ))}
            </div>
          ) : (
            <p className="t-small t-mute" style={{ margin: 0 }}>No ingredients available.</p>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function IngredientRow({ ing }: { ing: ScannerIngredient }) {
  const flags: { label: string; tone: Tone }[] = [];
  const irr = getIrritantLevel(ing);
  if (irr === 'likely')   flags.push({ label: 'Irritant (likely)',   tone: 'danger' });
  if (irr === 'possible') flags.push({ label: 'Irritant (possible)', tone: 'warn'   });
  if (ing.photosensitizer)           flags.push({ label: 'Photosensitizer',    tone: 'warn'   });
  if (ing.fragrance)                 flags.push({ label: 'Fragrance',          tone: 'neutral' });
  if (ing.preservative)              flags.push({ label: 'Preservative',       tone: 'neutral' });
  const comedogenic = comedogenicScore(ing);
  if (comedogenic !== null && comedogenic > 0)
    flags.push({ label: `Comedogenic ${comedogenic}/5`, tone: 'danger' });
  if (ing.active) flags.push({ label: 'Active', tone: 'ok' });

  const dotColor = ing.active
    ? 'var(--c-kivo-teal)'
    : irr === 'likely' || irr === 'possible'
      ? 'var(--c-coral)'
      : 'var(--c-charcoal-30)';

  return (
    <Link
      to={`/ingredients/${ing.id ?? ''}`}
      target="_blank"
      rel="noopener noreferrer"
      className="card card--compact card--bordered card--flat card--interactive"
      style={{ padding: 'var(--s-3)', display: 'block', minWidth: 0, textDecoration: 'none' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--s-2)' }}>
        <div style={{ minWidth: 0 }}>
          <div
            className="card__name"
            style={{
              fontFamily: 'var(--f-text)', fontSize: 'var(--fs-small)', fontWeight: 500,
              color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              transition: 'color var(--t-fast) var(--ease)',
            }}
          >
            {ingredientDisplayName(ing)}
          </div>
          {(ing.functions?.length ?? 0) > 0 ? (
            <div
              className="t-mute"
              style={{
                fontSize: 10, marginTop: 2,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {(ing.functions ?? []).join(', ')}
            </div>
          ) : null}
        </div>
        <span
          aria-hidden="true"
          style={{
            marginTop: 3, width: 10, height: 10, borderRadius: '50%',
            background: dotColor, flexShrink: 0, display: 'block',
          }}
        />
      </div>

      {flags.length > 0 ? (
        <div style={{ marginTop: 'var(--s-1)', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {flags.map((f, idx) => (
            <KivoBadge key={idx} tone={f.tone}>{f.label}</KivoBadge>
          ))}
        </div>
      ) : null}
    </Link>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24" width={18} height={18}
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      aria-hidden="true"
      style={{
        color: 'var(--ink-muted)', flexShrink: 0,
        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: `transform var(--t-fast) var(--ease)`,
      }}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}
